import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import WeatherSlide from "../UI/widgets/WeatherSlide";
import ExchangeRateSlide from "../UI/widgets/ExchangeRateSlide";
import CountdownSlide from "../UI/widgets/CountdownSlide";
import TopSocialFlipCard from "../UI/widgets/TopSocialFlipCard";
import AdvertisementModal from "../adversting/AdvertisementModal";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdvertisements } from "../../store/actions/adversitementsActions";
import { resetAdvertisementsState } from "../../store/reducers/adversitementsReducers";
import { useLocation } from "react-router-dom";

// Animation variants for the bento tiles
const tileVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.15 },
    }),
};

// Main RouteList Component
function RouteList() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const { advertisements } = useSelector((state) => state.advertisements);
    const [showAdModal, setShowAdModal] = useState(false);
    const inactivityTime = 60 * 1000; // 1 minute
    let inactivityTimer;

    // Device token
    const deviceToken = localStorage.getItem("deviceToken");

    // Очистка истории прохода: на главной странице сбрасываем позицию в стеке истории,
    // чтобы из неё нельзя было «Назад» вернуться в сессию предыдущего пользователя.
    useEffect(() => {
        if ((window.history.state?.idx ?? 0) > 0) {
            window.history.replaceState(
                { ...window.history.state, idx: 0 },
                "",
            );
        }
    }, []);

    // Сброс языка следующему пользователю: выбор ru/en, сделанный во время
    // прошлой сессии, не должен переходить по наследству — каждый заход на
    // главную (в т.ч. после завершённой печати) снова стартует с казахского.
    // Только mount-эффект (не в теле рендера) — иначе он бы перебивал выбор
    // языка через Header, пока пользователь ещё стоит на главном экране.
    useEffect(() => {
        if (i18n.language !== "kk") {
            i18n.changeLanguage("kk");
        }
    }, [i18n]);

    // Function to reset inactivity timer
    const resetTimer = () => {
        clearTimeout(inactivityTimer);
        if (location.pathname === "/") {
            inactivityTimer = setTimeout(async () => {
                if (!showAdModal) {
                    try {
                        const ads = await dispatch(
                            fetchAdvertisements(deviceToken),
                        );
                        if (ads && ads.payload.advertisements.length > 0) {
                            setShowAdModal(true);
                        }
                    } catch (err) {
                        console.error("Error fetching advertisements:", err);
                    }
                }
            }, inactivityTime);
        }
    };

    // Set up event listeners for activity and check page location
    useEffect(() => {
        const events = ["mousemove", "keydown", "touchstart", "scroll"];
        events.forEach((event) => window.addEventListener(event, resetTimer));

        // Reset timer when location changes
        resetTimer();

        return () => {
            clearTimeout(inactivityTimer);
            events.forEach((event) =>
                window.removeEventListener(event, resetTimer),
            );
        };
    }, [location.pathname, showAdModal]);

    // Fetch ads on mount if cached in localStorage
    useEffect(() => {
        const cachedAds = JSON.parse(localStorage.getItem("cachedAds"));
        if (cachedAds && cachedAds.advertisements) {
            dispatch({
                type: "advertisements/fetch/fulfilled",
                payload: cachedAds,
            });
        } else {
            dispatch(fetchAdvertisements(deviceToken));
        }
    }, [dispatch]);

    // Update localStorage when advertisements change
    useEffect(() => {
        if (advertisements) {
            localStorage.setItem("cachedAds", JSON.stringify(advertisements));
        }
    }, [advertisements]);

    // Reset advertisements state when leaving the main page
    localStorage.removeItem("access_token");
    localStorage.removeItem("role");
    useEffect(() => {
        if (location.pathname !== "/") {
            dispatch(resetAdvertisementsState());
            setShowAdModal(false);
            clearTimeout(inactivityTimer);
        }
    }, [location.pathname, dispatch]);

    // Close modal and reset timer
    const handleCloseModal = () => {
        setShowAdModal(false);
        resetTimer();
    };

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col bg-[#EDEFF4]">
            <div className="flex-1 min-h-0 grid grid-cols-8 grid-rows-[1fr_1fr_0.6fr_0.6fr] gap-[1.6vh]">
                {/* Главная CTA-плитка — распечатка */}
                <motion.div
                    className="col-span-6 row-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                >
                    <Link
                        to="/printing"
                        className="w-full h-full rounded-[3vh] bg-[#246CE2] text-white p-[4vh_3vw] flex flex-col justify-between relative overflow-hidden"
                    >
                        <div className="w-[7vh] h-[7vh] rounded-[1.6vh] bg-white/15 flex items-center justify-center">
                            <svg
                                className="w-[3.8vh] h-[3.8vh]"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="#fcfcfc"
                                    d="M8.616 20q-.667 0-1.141-.475T7 18.386V16H5.192q-.666 0-1.14-.475t-.475-1.14v-3.77q0-.85.577-1.424q.577-.576 1.423-.576h12.846q.85 0 1.425.576t.575 1.424v3.77q0 .666-.474 1.14T18.808 16H17v2.385q0 .666-.475 1.14t-1.14.475zM17 7.846H7V6.227q0-.667.475-1.14q.474-.472 1.14-.472h6.77q.666 0 1.14.475q.475.475.475 1.14zm.616 4.27q.425 0 .712-.288t.288-.712t-.288-.713t-.712-.288t-.713.288t-.287.713t.287.712t.713.288m-9 6.884h6.769q.269 0 .442-.173t.173-.442v-3.308q0-.27-.173-.442q-.173-.173-.442-.173h-6.77q-.269 0-.442.172q-.173.174-.173.443v3.308q0 .269.173.442t.443.173"
                                />
                            </svg>
                        </div>
                        <div className="pr-[9vw]">
                            <h2 className="text-[4.6vh] font-extrabold tracking-tight mb-[0.8vh]">
                                {t("routeList.main_title")}
                            </h2>
                            <p className="text-[1.9vh] text-white/75 font-medium leading-snug">
                                {t("routeList.main_subtitle")}
                            </p>
                        </div>
                        <motion.div
                            className="absolute right-[3vw] bottom-[3vh] w-[6vh] h-[6vh] rounded-full bg-white flex items-center justify-center"
                            animate={{ x: ["0vw", "1.4vw", "0vw"] }}
                            transition={{
                                duration: 2.4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <svg
                                className="w-[2.6vh] h-[2.6vh]"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#246CE2"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </motion.div>
                    </Link>
                </motion.div>

                {/* Погода */}
                <motion.div
                    className="col-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                >
                    <WeatherSlide />
                </motion.div>

                {/* Курс USD/KZT */}
                <motion.div
                    className="col-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                >
                    <ExchangeRateSlide />
                </motion.div>

                {/* Топ пользователей / Соцсети — карточка с флипом */}
                <motion.div
                    className="col-span-4 row-span-2"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                >
                    <TopSocialFlipCard />
                </motion.div>

                {/* До сессии */}
                <motion.div
                    className="col-span-4"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={2}
                >
                    <CountdownSlide />
                </motion.div>

                {/* Место под рекламу */}
                <motion.div
                    className="col-span-4"
                    variants={tileVariants}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                >
                    <Link
                        to="/advertisement"
                        className="w-full h-full rounded-[3vh] border-[0.3vh] border-dashed border-[#C3C8D2] flex flex-row items-center justify-center gap-[1vw] text-[#9AA1AD]"
                    >
                        <svg
                            className="w-[2.4vh] h-[2.4vh]"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        >
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        <span className="text-[1.7vh] font-bold">
                            {t("routeList.ad_placeholder")}
                        </span>
                    </Link>
                </motion.div>
            </div>

            {/* Advertisement Modal */}
            {showAdModal && advertisements && advertisements.advertisements && (
                <AdvertisementModal onClose={handleCloseModal} />
            )}
        </div>
    );
}

export default RouteList;
